import { POWERS, UPGRADES } from "@roguelik/shared-sim";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "preact/hooks";
import { act, offer } from "../store.js";
import { KIND_META, RARITY_COLOR } from "../theme.js";

/** Phys/Magic badge meta for power cards (Phase B1). */
const DTYPE_META: Record<string, { label: string; color: string; icon: string }> = {
  physical: { label: "PHYS", color: "#ffb24a", icon: "⚔️" },
  magic: { label: "MAGIC", color: "#9a6bff", icon: "✦" },
};
/** The damage type of an upgrade's power, if it is a weapon upgrade (else undefined). */
function upgradeDamageType(id: number): "physical" | "magic" | undefined {
  const u = UPGRADES[id];
  if (u === undefined || u.power === undefined) return undefined;
  return POWERS[u.power]?.damageType;
}

/** Real-time level-up overlay: three tall portrait cards (rarity frame + kind badge). */
export function LevelUpOverlay() {
  const o = offer.value;
  const [now, setNow] = useState(Date.now());
  const deadline = o?.deadline ?? 0;
  // Tick a clock while a timed offer is up, so the countdown bar animates.
  useEffect(() => {
    if (o === null || deadline <= 0) return;
    let raf = 0;
    const loop = (): void => {
      setNow(Date.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [o, deadline]);
  const remainMs = deadline > 0 ? Math.max(0, deadline - now) : 0;
  const totalMs = 10_000;
  const frac = deadline > 0 ? Math.max(0, Math.min(1, remainMs / totalMs)) : 0;
  const secs = Math.ceil(remainMs / 1000);
  const pick = (i: number): void => {
    const choice = o?.choices[i];
    if (choice === undefined) return;
    act()?.choose(choice.id);
    offer.value = null;
  };
  return (
    <AnimatePresence>
      {o !== null && (
        <motion.div
          class="pointer absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#06090f]/55"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div class="text-2xl font-bold">Level {o.level} — choose an upgrade</div>
          {deadline > 0 && (
            <div class="flex flex-col items-center gap-1">
              <div class="text-sm opacity-75">Auto-picks in {secs}s</div>
              <div class="h-1.5 w-64 overflow-hidden rounded-full bg-white/15">
                <div
                  class="h-full rounded-full"
                  style={{
                    width: `${frac * 100}%`,
                    background: frac > 0.3 ? "#6fe0ff" : "#ff7a7a",
                  }}
                />
              </div>
            </div>
          )}
          <div class="flex gap-3.5">
            {o.choices.map((c, i) => {
              const accent = RARITY_COLOR[c.rarity ?? "common"] ?? RARITY_COLOR.common;
              const kind = KIND_META[c.kind ?? "passive"] ?? KIND_META.passive;
              return (
                <motion.button
                  key={c.id}
                  type="button"
                  onClick={() => pick(i)}
                  initial={{ opacity: 0, y: 24, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    delay: 0.05 + i * 0.06,
                    type: "spring",
                    stiffness: 320,
                    damping: 24,
                  }}
                  whileHover={{ y: -6, scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  class="flex h-62 w-42 cursor-pointer flex-col overflow-hidden rounded-2xl border-2 text-left"
                  style={{
                    width: "168px",
                    height: "248px",
                    borderColor: accent,
                    background: "linear-gradient(180deg,#1a2336,#0f1626)",
                    boxShadow: `0 0 18px ${accent}55, inset 0 0 0 1px #ffffff10`,
                  }}
                >
                  <div
                    class="flex items-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wide text-[#0b0f17]"
                    style={{ background: kind.color }}
                  >
                    {kind.icon} {kind.label}
                    {(() => {
                      const dt = upgradeDamageType(c.id);
                      const meta = dt ? DTYPE_META[dt] : undefined;
                      return meta ? (
                        <span
                          class="ml-auto rounded px-1.5 py-0.5 text-[10px] font-bold text-white"
                          style={{ background: meta.color }}
                        >
                          {meta.icon} {meta.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <div class="flex flex-1 flex-col gap-2 p-3">
                    <div class="text-[17px] font-bold leading-tight">{c.name}</div>
                    <div class="text-[13px] leading-snug opacity-80">{c.desc}</div>
                  </div>
                  <div class="flex items-center justify-between px-3 py-2.5">
                    <div
                      class="rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
                      style={{ color: accent, borderColor: accent }}
                    >
                      {c.rarity ?? "common"}
                    </div>
                    <div class="flex size-5 items-center justify-center rounded-md border border-white/30 text-xs opacity-60">
                      {i + 1}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
